/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../ReduxToolkit/Store';
import { fetchFolders, createFolder, deleteFolder, updateFolder } from '../../ReduxToolkit/FolderSlice';
import { documentService } from '../../Services/DocumentService';
import { assignToFolder, removeFromFolder, toggleFavorite as toggleFavoriteAction, fetchFavorites } from '../../ReduxToolkit/FavoriteSlice';
import { 
  Star, MessageSquareCode, DownloadCloud, AlertOctagon, FolderSymlink, 
  FolderOpen, Search, FileDown, Trash2, Calendar, Eye, EyeOff, ShieldCheck, 
  Check, Clock, Plus, KeyRound, Lock, AlertCircle, CheckCircle, UserCheck, 
  Edit3, X, FolderPlus, User, Sparkles, ArrowRight, RefreshCw 
} from 'lucide-react';
import { LegalDocument, UserNote, DownloadRecord, SuggestedDocument, Folder } from '../../types';
import { noteService } from '../../Services/NoteService';
import { authService } from '../../Services/AuthService';
import { suggestionService } from '../../Services/SuggestionService';
import { formulaService, UserFormulaInfo } from '../../Services/FormulaService';
import { downloadService } from '../../Services/DownloadService';
import apiClient from '../../Api/ApiClient';
import { toast } from 'react-toastify';
import { StatusBadge } from '../ui/StatusBadge';
import { formatFrenchDate } from '../../utils/legalUtils';

interface UserDashboardViewProps {
  documents?: LegalDocument[];
  setView?: (view: string) => void;
  setActiveDocumentId?: (id: string | number | null) => void;
  favorites?: (string | number)[];
  toggleFavorite?: (id: string | number) => void;
  downloads?: DownloadRecord[];
  suggestions?: SuggestedDocument[];
}

export const UserDashboardView: React.FC<UserDashboardViewProps> = ({
  documents = [],
  setView = () => {},
  setActiveDocumentId = () => {},
  favorites = [],
  toggleFavorite = () => {},
  downloads = [],
  suggestions: suggestionsProp = []
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const dispatch = useDispatch<AppDispatch>();
  const favoritesState = useSelector((state: RootState) => state.favorites.items || []);
  const foldersState = useSelector((state: RootState) => state.folders.items || []) as Folder[];
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState<'FAVORIS' | 'NOTES' | 'DOWNLOADS' | 'SUGGESTIONS' | 'SECURITY'>('FAVORIS');

  // Formule active & Quota de consultation dynamique
  const [userFormula, setUserFormula] = React.useState<UserFormulaInfo | null>(null);
  const [discoveryQuota, setDiscoveryQuota] = React.useState<number>(5);

  React.useEffect(() => {
    formulaService.getDiscoveryQuota().then(info => {
      if (info && typeof info.maxConsultations === 'number' && info.maxConsultations > 0) {
        setDiscoveryQuota(info.maxConsultations);
      }
    });

    if (currentUser) {
      formulaService.getMyFormula().then(formula => {
        if (formula) {
          setUserFormula(formula);
        }
      });
    }
  }, [currentUser]);

  // Real Downloads state & loading
  const [userDownloads, setUserDownloads] = React.useState<DownloadRecord[]>(downloads || []);
  const [loadingDownloads, setLoadingDownloads] = React.useState<boolean>(false);
  const [downloadsSearch, setDownloadsSearch] = React.useState<string>('');
  const [downloadingDocId, setDownloadingDocId] = React.useState<string | null>(null);

  const loadUserDownloads = React.useCallback(async () => {
    if (!currentUser) return;
    setLoadingDownloads(true);
    try {
      const records = await downloadService.getMyDownloads();
      setUserDownloads(records);
    } catch (err) {
      console.error("Erreur de chargement des téléchargements:", err);
    } finally {
      setLoadingDownloads(false);
    }
  }, [currentUser]);

  React.useEffect(() => {
    loadUserDownloads();
  }, [loadUserDownloads]);

  // Change password state
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const [pwdError, setPwdError] = React.useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = React.useState<string | null>(null);

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    setPwdSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwdError("Veuillez renseigner tous les champs du mot de passe.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPwdError("Le nouveau mot de passe et sa confirmation ne correspondent pas.");
      return;
    }

    if (newPassword.length < 6) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setPwdLoading(true);
    try {
      const res = await authService.changePassword({
        currentPassword,
        newPassword,
        confirmPassword
      });
      if (res.success) {
        setPwdSuccess(res.message || "Votre mot de passe a été modifié avec succès.");
        toast.success("Mot de passe modifié avec succès !");
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwdError(res.message || "Erreur lors de la modification du mot de passe.");
      }
    } catch (err: any) {
      setPwdError(err.message || err.data?.message || "Erreur de communication avec le serveur.");
    } finally {
      setPwdLoading(false);
    }
  };
  
  // Real Folders & Favorites state
  const [selectedFolderId, setSelectedFolderId] = React.useState<string>('TOUS');
  const [newFolderName, setNewFolderName] = React.useState<string>('');
  const [newFolderDesc, setNewFolderDesc] = React.useState<string>('');
  const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);
  const [editingFolderId, setEditingFolderId] = React.useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = React.useState<string>('');
  const [selectedDocIdForFolder, setSelectedDocIdForFolder] = React.useState<string | number | null>(null);

  // Search state inside notes
  const [notesSearch, setNotesSearch] = React.useState<string>('');

  // Fetch favorites and folders on component mount if logged in
  React.useEffect(() => {
    if (currentUser) {
      dispatch(fetchFavorites());
      dispatch(fetchFolders());
    }
  }, [dispatch, currentUser]);

  // Load private notes from backend
  const [userNotes, setUserNotes] = React.useState<UserNote[]>([]);
  React.useEffect(() => {
    let isMounted = true;
    if (currentUser) {
      noteService.getMyNotes().then(notes => {
        if (isMounted && Array.isArray(notes)) setUserNotes(notes);
      });
    }
    return () => { isMounted = false; };
  }, [currentUser]);

  // Load user suggestions from backend (no hardcoded mock data)
  const [userSuggestions, setUserSuggestions] = React.useState<SuggestedDocument[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    if (currentUser) {
      setLoadingSuggestions(true);
      suggestionService.getMySuggestions().then(items => {
        if (isMounted) {
          setUserSuggestions(items || []);
          setLoadingSuggestions(false);
        }
      }).catch(() => {
        if (isMounted) setLoadingSuggestions(false);
      });
    }
    return () => { isMounted = false; };
  }, [currentUser]);

  // Export simulator
  const handleSimulateExport = (filename: string, content: string) => {
    alert(`📥 Simulation d'export effectuée !\n\nNom du fichier : ${filename}.txt\nLe fichier a été téléchargé avec succès.`);
  };

  const handleAddFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    setIsCreatingFolder(true);
    try {
      await dispatch(createFolder({ name: trimmed, description: newFolderDesc.trim() })).unwrap();
      toast.success(`Dossier "${trimmed}" créé avec succès !`);
      setNewFolderName('');
      setNewFolderDesc('');
      dispatch(fetchFolders());
    } catch (err: any) {
      toast.error(err?.message || "Erreur lors de la création du dossier.");
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId: string, folderName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Voulez-vous supprimer le dossier "${folderName}" ? Vos documents favoris resteront conservés.`)) {
      try {
        await dispatch(deleteFolder(folderId)).unwrap();
        if (selectedFolderId === folderId) {
          setSelectedFolderId('TOUS');
        }
        toast.success(`Dossier "${folderName}" supprimé.`);
        dispatch(fetchFolders());
        dispatch(fetchFavorites());
      } catch (err: any) {
        toast.error(err?.message || "Erreur lors de la suppression du dossier.");
      }
    }
  };

  const handleStartEditFolder = (folder: Folder, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  };

  const handleSaveEditFolder = async (folderId: string, e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = editingFolderName.trim();
    if (!trimmed) return;
    try {
      await dispatch(updateFolder({ id: folderId, name: trimmed })).unwrap();
      toast.success("Dossier renommé avec succès !");
      setEditingFolderId(null);
      dispatch(fetchFolders());
    } catch (err: any) {
      toast.error(err?.message || "Erreur lors de la mise à jour du dossier.");
    }
  };

  // Map doc ID to FavoriteDto
  const docFavoriteMap = React.useMemo(() => {
    const map = new Map<string, any>();
    (favoritesState || []).forEach((f: any) => {
      if (f && f.documentId) {
        map.set(String(f.documentId).toLowerCase().trim(), f);
      }
    });
    return map;
  }, [favoritesState]);

  // Load document details for all favorites
  const [localDocs, setLocalDocs] = React.useState<LegalDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = React.useState<boolean>(false);

  React.useEffect(() => {
    let isMounted = true;
    const fetchDocs = async () => {
      if (!favoritesState || favoritesState.length === 0) {
        if (isMounted) setLocalDocs([]);
        return;
      }
      setLoadingDocs(true);
      try {
        const docIds = Array.from(new Set(favoritesState.map((f: any) => String(f.documentId).trim())));
        const promises = docIds.map(id => documentService.getDocumentById(id));
        const results = await Promise.all(promises);
        if (isMounted) {
          setLocalDocs(results.filter(Boolean) as LegalDocument[]);
        }
      } catch (err) {
        console.error("Error fetching favorited docs:", err);
      } finally {
        if (isMounted) setLoadingDocs(false);
      }
    };
    fetchDocs();
    return () => { isMounted = false; };
  }, [favoritesState]);

  const favoritedDocs = React.useMemo(() => {
    return localDocs.filter(doc => docFavoriteMap.has(String(doc.id).toLowerCase().trim()));
  }, [localDocs, docFavoriteMap]);

  const filteredFavoritedDocs = React.useMemo(() => {
    if (selectedFolderId === 'TOUS') {
      return favoritedDocs;
    }
    return favoritedDocs.filter(doc => {
      const fav = docFavoriteMap.get(String(doc.id).toLowerCase().trim());
      return fav && fav.folderIds && fav.folderIds.some((fid: string) => String(fid).toLowerCase().trim() === String(selectedFolderId).toLowerCase().trim());
    });
  }, [favoritedDocs, selectedFolderId, docFavoriteMap]);

  const getDocFolders = (docId: string | number): Folder[] => {
    const fav = docFavoriteMap.get(String(docId).toLowerCase().trim());
    if (!fav || !fav.folderIds || fav.folderIds.length === 0) return [];
    return foldersState.filter(folder => 
      fav.folderIds.some((fid: string) => String(fid).toLowerCase().trim() === String(folder.id).toLowerCase().trim())
    );
  };

  const handleAssignToFolder = async (docId: string | number, folderId: string) => {
    if (!folderId) return;
    try {
      await dispatch(assignToFolder({ documentId: docId, folderId })).unwrap();
      toast.success("Document classé dans le dossier !");
      setSelectedDocIdForFolder(null);
      dispatch(fetchFavorites());
      dispatch(fetchFolders());
    } catch (err: any) {
      toast.error(err?.message || "Erreur lors du classement.");
    }
  };

  const handleRemoveFromFolder = async (docId: string | number, folderId: string, folderName?: string) => {
    try {
      await dispatch(removeFromFolder({ documentId: docId, folderId })).unwrap();
      toast.info(`Document retiré de "${folderName || 'ce dossier'}".`);
      dispatch(fetchFavorites());
      dispatch(fetchFolders());
    } catch (err: any) {
      toast.error(err?.message || "Erreur lors du retrait du dossier.");
    }
  };

  const currentSelectedFolderName = React.useMemo(() => {
    if (selectedFolderId === 'TOUS') return "Tous les favoris";
    const found = foldersState.find(f => String(f.id).toLowerCase().trim() === String(selectedFolderId).toLowerCase().trim());
    return found ? found.name : "Dossier";
  }, [selectedFolderId, foldersState]);

  const currentSelectedFolder = React.useMemo(() => {
    if (selectedFolderId === 'TOUS') return null;
    return foldersState.find(f => String(f.id).toLowerCase().trim() === String(selectedFolderId).toLowerCase().trim()) || null;
  }, [selectedFolderId, foldersState]);

  const filteredNotes = userNotes.filter(note => {
    const searchString = `${note.content} ${note.documentTitle || ''}`.toLowerCase();
    return searchString.includes(notesSearch.toLowerCase());
  });

  const groupedNotes = React.useMemo(() => {
    const groups: Record<string, UserNote[]> = {};
    for (const note of filteredNotes) {
      if (!groups[note.documentId]) groups[note.documentId] = [];
      groups[note.documentId].push(note);
    }
    return Object.entries(groups)
      .map(([documentId, notes]) => ({
        documentId,
        documentTitle: notes[0].documentTitle || "Texte inconnu",
        referenceNumber: notes[0].referenceNumber,
        notes: notes.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      }))
      .sort((a, b) => new Date(b.notes[0].updatedAt).getTime() - new Date(a.notes[0].updatedAt).getTime());
  }, [filteredNotes]);

  // Actions Téléchargements
  const handleDownloadAgain = async (dl: DownloadRecord) => {
    const docId = String(dl.documentId);
    setDownloadingDocId(docId);
    try {
      const ticket = await documentService.requestDownloadTicket(docId);
      const blob = await apiClient.getBlob(`/api/documents/${docId}/download-stream?ticket=${ticket}`);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const safeName = (dl.referenceNumber || dl.officialTitle || 'document_juridique')
        .replace(/[\/\\:*?"<>|]/g, '_')
        .substring(0, 100);
      link.download = `${safeName}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Téléchargement du document réussi !");

      // Enregistrer de nouveau et actualiser la liste
      await downloadService.recordDownload(docId);
      loadUserDownloads();
    } catch (error: any) {
      if (error?.status === 429) {
        toast.warning("Vous avez atteint votre quota de téléchargement journalier.");
      } else if (error?.status === 401 || error?.status === 403) {
        toast.error("Votre formule actuelle ne permet pas de télécharger ce document.");
      } else {
        toast.info("Accès direct à la fiche de l'acte...");
        navigate(`/document/${docId}`);
      }
    } finally {
      setDownloadingDocId(null);
    }
  };

  const filteredDownloads = React.useMemo(() => {
    if (!downloadsSearch.trim()) return userDownloads;
    const q = downloadsSearch.toLowerCase().trim();
    return userDownloads.filter(dl => 
      (dl.officialTitle && dl.officialTitle.toLowerCase().includes(q)) ||
      (dl.referenceNumber && dl.referenceNumber.toLowerCase().includes(q)) ||
      (dl.categoryName && dl.categoryName.toLowerCase().includes(q))
    );
  }, [userDownloads, downloadsSearch]);

  const TabButton = ({ id, icon: Icon, label, count }: { id: any, icon: any, label: string, count: number }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer flex-shrink-0 ${activeTab === id ? 'bg-white shadow-md border-b-2 border-violet-600 text-violet-700' : 'hover:bg-zinc-200/50 text-zinc-600 border-b-2 border-transparent hover:border-zinc-300'}`}
    >
      <Icon className={`w-5 h-5 ${activeTab === id ? 'text-violet-600' : 'text-zinc-500'}`} />
      <span className="font-bold text-sm tracking-wide">{label}</span>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${activeTab === id ? 'bg-violet-100 text-violet-700' : 'bg-zinc-200 text-zinc-500'}`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

      {/* DASHBOARD HEADER - Gradient Banner */}
      <div className="bg-gradient-to-r from-primary-900 via-primary-800 to-primary-750 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-500/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-[08px] font-bold uppercase tracking-widest backdrop-blur-sm text-white">
              <ShieldCheck className="w-3.5 h-3.5 text-violet-200" /> Espace Personnel Sécurisé
            </div>
            <h4 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3 text-white">
              <FolderSymlink className="w-8 h-8 text-violet-100" /> Bienvenue {currentUser?.fullName || 'Abonné'}!
            </h4>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-5 py-4 rounded-2xl flex items-center gap-4">
             <div className="w-12 h-12 bg-violet-500/30 rounded-full flex items-center justify-center text-white">
               <User className="w-6 h-6" />
             </div>
             <div>
               <div className="text-xs text-violet-200 font-bold uppercase tracking-wider">{currentUser?.fullName || 'Abonné'}</div>
               <div className="font-display font-bold text-lg text-white">{currentUser?.cabinetName || 'Cabinet'}</div>
             </div>
          </div>
        </div>

        {/* Dynamic Formula & Quota Bar */}
        <div className="relative z-10 mt-6 pt-5 border-t border-white/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-400/20 text-amber-300 flex items-center justify-center border border-amber-400/30 shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-violet-200">
                Formule d'abonnement
              </div>
              <div className="text-sm font-bold text-white flex items-center gap-2">
                <span>{userFormula?.groupName || 'Découverte'}</span>
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 font-semibold">
                  Active
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
            <div className="text-left sm:text-right">
              <div className="text-[10px] uppercase font-bold tracking-wider text-violet-200">
                Quota de consultation
              </div>
              <div className="text-sm font-mono font-bold text-amber-300">
                {userFormula?.isUnlimitedConsultations || (userFormula && userFormula.maxConsultations < 0) ? (
                  'Consultations Illimitées'
                ) : (
                  `${userFormula?.consultationsUsed ?? 0} / ${userFormula?.maxConsultations ?? discoveryQuota} actes consultés`
                )}
              </div>
            </div>

            {(!userFormula?.isUnlimitedConsultations && (!userFormula || userFormula.maxConsultations > 0)) && (
              <div className="w-24 sm:w-32 bg-white/20 h-2 rounded-full overflow-hidden hidden md:block">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    ((userFormula?.consultationsUsed ?? 0) / (userFormula?.maxConsultations ?? discoveryQuota)) >= 1
                      ? 'bg-red-400'
                      : ((userFormula?.consultationsUsed ?? 0) / (userFormula?.maxConsultations ?? discoveryQuota)) >= 0.7
                        ? 'bg-amber-400'
                        : 'bg-emerald-400'
                  }`}
                  style={{
                    width: `${Math.min(100, Math.round(((userFormula?.consultationsUsed ?? 0) / (userFormula?.maxConsultations ?? discoveryQuota)) * 100))}%`
                  }}
                />
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate('/formules')}
              className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-bold text-white transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
              title="Voir et changer de formule"
            >
              <span>Formules</span>
              <ArrowRight className="w-3.5 h-3.5 text-amber-300" />
            </button>
          </div>
        </div>
      </div>

      {/* DASHBOARD TAB SELECTORS */}
      <div className="flex bg-zinc-100/80 p-2 rounded-2xl gap-2 overflow-x-auto border border-primary-750/5 shadow-inner">
        <TabButton id="FAVORIS" icon={Star} label="Dossiers & Favoris" count={favoritedDocs.length} />
        <TabButton id="NOTES" icon={MessageSquareCode} label="Notes Privées" count={userNotes.length} />
        <TabButton id="DOWNLOADS" icon={DownloadCloud} label="Téléchargements" count={userDownloads.length} />
        <TabButton id="SUGGESTIONS" icon={AlertOctagon} label="Mes Requêtes" count={userSuggestions.length} />
        <TabButton id="SECURITY" icon={KeyRound} label="Sécurité & Mot de passe" count={0} />
      </div>

      {/* 1. FAVORITES PANEL WITH FOLDER SEGREGATION */}
      {activeTab === 'FAVORIS' && (
        <div className="animate-fade-in space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Col: Folders Manager */}
            <div className="lg:col-span-4 bg-white border border-primary-750/10 shadow-xl shadow-primary-750/5 rounded-3xl p-6 space-y-6 self-start">
              <div className="border-b border-primary-550/10 pb-4">
                <h4 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-amber-500" /> Classeurs Intellectuels
                </h4>
                <p className="text-xs text-zinc-300 mt-1">Organisez vos documents de travail par dossiers personnalisés.</p>
              </div>

              <div className="flex flex-col gap-2">
                {/* Global 'TOUS' option */}
                <button
                  type="button"
                  onClick={() => setSelectedFolderId('TOUS')}
                  className={`text-left text-sm p-3 rounded-xl flex items-center justify-between font-bold cursor-pointer transition-all ${
                    selectedFolderId === 'TOUS' 
                      ? 'bg-gradient-to-r from-primary-750 to-primary-600 text-white shadow-md' 
                      : 'hover:bg-primary-50 text-zinc-700 border border-transparent hover:border-primary-100'
                  }`}
                >
                  <span className="truncate flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-amber-500" /> Tous mes documents
                  </span>
                  <span className={`text-[10px] w-6 h-6 flex items-center justify-center rounded-full font-extrabold ${
                    selectedFolderId === 'TOUS' ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'
                  }`}>
                    {favoritedDocs.length}
                  </span>
                </button>

                {/* Real Folders from Backend */}
                {foldersState.map((folder) => {
                  const count = favoritedDocs.filter(d => {
                    const fav = docFavoriteMap.get(String(d.id).toLowerCase().trim());
                    return fav && fav.folderIds && fav.folderIds.some((fid: string) => String(fid).toLowerCase().trim() === String(folder.id).toLowerCase().trim());
                  }).length;

                  const isSelected = selectedFolderId === folder.id;
                  const isEditing = editingFolderId === folder.id;

                  if (isEditing) {
                    return (
                      <form
                        key={folder.id}
                        onSubmit={(e) => handleSaveEditFolder(folder.id, e)}
                        className="flex items-center gap-1.5 p-1.5 bg-amber-50 border border-amber-300 rounded-xl shadow-inner"
                      >
                        <input
                          type="text"
                          autoFocus
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          className="text-xs p-1.5 flex-grow bg-white border border-amber-300 rounded-lg outline-none font-bold text-zinc-900"
                        />
                        <button
                          type="submit"
                          className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer"
                          title="Enregistrer"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingFolderId(null)}
                          className="p-1.5 bg-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-300 cursor-pointer"
                          title="Annuler"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </form>
                    );
                  }

                  return (
                    <div
                      key={folder.id}
                      onClick={() => setSelectedFolderId(folder.id)}
                      className={`group/item text-left text-sm p-3 rounded-xl flex items-center justify-between font-bold cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-gradient-to-r from-primary-750 to-primary-600 text-white shadow-md' 
                          : 'hover:bg-primary-50 text-zinc-700 border border-transparent hover:border-primary-100'
                      }`}
                    >
                      <div className="truncate flex flex-col gap-0.5 mr-2">
                        <span className="truncate flex items-center gap-2">
                          <FolderOpen className="w-4 h-4 text-amber-500" /> {folder.name}
                        </span>
                        {folder.createdOn && (
                          <span className={`text-[10px] font-medium flex items-center gap-1 ${
                            isSelected ? 'text-violet-200' : 'text-zinc-400'
                          }`}>
                            <Clock className="w-2.5 h-2.5" />
                            {formatFrenchDate(folder.createdOn)}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* Edit button */}
                        <button
                          type="button"
                          onClick={(e) => handleStartEditFolder(folder, e)}
                          className={`opacity-0 group-hover/item:opacity-100 p-1 rounded-lg transition-opacity ${
                            isSelected ? 'hover:bg-white/20 text-white' : 'hover:bg-zinc-200 text-zinc-500'
                          }`}
                          title="Renommer le dossier"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={(e) => handleDeleteFolder(folder.id, folder.name, e)}
                          className={`opacity-0 group-hover/item:opacity-100 p-1 rounded-lg transition-opacity ${
                            isSelected ? 'hover:bg-red-500/30 text-red-200 hover:text-white' : 'hover:bg-red-50 text-zinc-400 hover:text-red-600'
                          }`}
                          title="Supprimer le dossier"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        {/* Count */}
                        <span className={`text-[10px] min-w-6 h-6 px-1.5 flex items-center justify-center rounded-full font-extrabold ${
                          isSelected ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'
                        }`}>
                          {count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add folder inside side menu */}
              <form onSubmit={handleAddFolder} className="pt-4 border-t border-zinc-100 space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Créer un nouveau Dossier</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      required
                      placeholder="Nom du dossier client..."
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      disabled={isCreatingFolder}
                      className="w-full text-xs p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-semibold"
                    />
                    <button 
                      type="submit" 
                      disabled={isCreatingFolder || !newFolderName.trim()}
                      className="bg-primary-750 hover:bg-primary-800 disabled:opacity-50 text-white text-xs px-4 rounded-xl cursor-pointer font-bold shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center"
                      title="Créer"
                    >
                      {isCreatingFolder ? "..." : <Plus className="w-4 h-4" />}
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Description ou client (optionnel)..."
                    value={newFolderDesc}
                    onChange={(e) => setNewFolderDesc(e.target.value)}
                    className="w-full text-[11px] p-2 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-zinc-600"
                  />
                </div>
              </form>

            </div>

            {/* Right Col: filtered results list */}
            <div className="lg:col-span-8 space-y-4">
              <div className="bg-white border border-primary-750/10 rounded-3xl p-6 shadow-xl shadow-primary-750/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-display font-extrabold text-xl text-zinc-900 capitalize">
                    Dossier : <span className="text-primary-750">{currentSelectedFolderName}</span>
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500 font-medium mt-0.5">
                    <span>{filteredFavoritedDocs.length} acte(s) répertorié(s) dans cette chemise.</span>
                    {currentSelectedFolder?.createdOn && (
                      <>
                        <span>â€¢</span>
                        <span className="flex items-center gap-1 text-zinc-400">
                          <Clock className="w-3 h-3" /> Créé le {formatFrenchDate(currentSelectedFolder.createdOn)}
                        </span>
                      </>
                    )}
                  </div>
                  {currentSelectedFolder?.description && (
                    <p className="text-xs text-zinc-500 italic mt-1 bg-zinc-50 p-2 rounded-lg border border-zinc-100">
                      {currentSelectedFolder.description}
                    </p>
                  )}
                </div>
                {filteredFavoritedDocs.length > 0 && (
                  <button
                    onClick={() => handleSimulateExport(`Favoris_${currentSelectedFolderName}`, filteredFavoritedDocs.map(d => `${d.nature} - ${d.referenceNumber}: ${d.officialTitle}`).join("\n\n"))}
                    className="flex items-center gap-2 bg-zinc-100 hover:bg-primary-50 text-zinc-700 hover:text-primary-750 text-xs px-4 py-2.5 rounded-xl font-bold cursor-pointer transition-all border border-transparent hover:border-primary-200 shadow-sm shrink-0"
                  >
                    <FileDown className="w-4 h-4" /> Exporter le bordereau
                  </button>
                )}
              </div>

              {loadingDocs ? (
                <div className="bg-white border border-primary-750/10 rounded-3xl p-16 text-center space-y-3 shadow-sm flex flex-col items-center justify-center">
                  <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-xs font-semibold text-zinc-500">Chargement de vos favoris...</p>
                </div>
              ) : filteredFavoritedDocs.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-primary-750/10 rounded-3xl p-16 text-center space-y-4 shadow-sm flex flex-col items-center justify-center">
                  <div className="bg-primary-50 p-4 rounded-full"><FolderOpen className="w-8 h-8 text-primary-500" /></div>
                  <p className="text-sm font-semibold text-zinc-500">
                    {selectedFolderId === 'TOUS' 
                      ? "Vous n'avez aucun document dans vos favoris pour le moment." 
                      : `Aucun document classé dans le dossier "${currentSelectedFolderName}".`}
                  </p>
                  {selectedFolderId !== 'TOUS' && (
                    <button
                      onClick={() => setSelectedFolderId('TOUS')}
                      className="text-xs font-bold bg-white text-primary-750 border border-primary-200 px-4 py-2 rounded-full hover:bg-primary-50 transition-colors shadow-sm cursor-pointer"
                    >
                      Retourner à l'index global de mes favoris
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredFavoritedDocs.map((doc) => {
                    const docFolders = getDocFolders(doc.id);
                    const fav = docFavoriteMap.get(String(doc.id).toLowerCase().trim());

                    return (
                      <div
                        key={doc.id}
                        className="group bg-white p-6 border border-primary-750/10 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-6 shadow-sm hover:shadow-xl hover:shadow-primary-750/5 transition-all duration-300 hover:-translate-y-0.5"
                      >
                        <div className="space-y-3 flex-grow">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-mono font-bold bg-primary-100 text-primary-800 px-2.5 py-1 border border-primary-200/50 rounded-md shadow-sm">
                              {doc.nature}
                            </span>
                            <span className="text-[10px] font-mono font-bold text-zinc-600 bg-zinc-100 border border-zinc-200 px-2.5 py-1 rounded-md">
                              Réf: {doc.referenceNumber}
                            </span>
                            
                            {/* Folder badges */}
                            {docFolders.length === 0 ? (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-zinc-500 bg-zinc-100 border border-zinc-200 px-2.5 py-1 rounded-md">
                                <FolderOpen className="w-3 h-3 text-zinc-400" /> Non classé
                              </span>
                            ) : (
                              docFolders.map(f => (
                                <span
                                  key={f.id}
                                  className="flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-md shadow-sm"
                                >
                                  <FolderOpen className="w-3 h-3 text-amber-600" />
                                  <span>{f.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFromFolder(doc.id, f.id, f.name)}
                                    className="ml-1 text-amber-400 hover:text-red-600 transition-colors cursor-pointer"
                                    title={`Retirer du dossier "${f.name}"`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))
                            )}
                          </div>
                          <h5 
                            onClick={() => navigate(`/document/${doc.id}`)}
                            className="font-display font-bold text-base text-zinc-900 line-clamp-2 leading-snug group-hover:text-primary-750 transition-colors cursor-pointer"
                          >
                            {doc.officialTitle}
                          </h5>
                          {(doc.dateOfAdoption || doc.effectiveDate) && (
                            <div className="flex items-center gap-1 text-[11px] text-zinc-400">
                              <Clock className="w-3 h-3" /> Adopté le {formatFrenchDate(doc.dateOfAdoption || doc.effectiveDate)}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0 border-t sm:border-t-0 sm:border-l border-zinc-100 pt-4 sm:pt-0 sm:pl-6">
                          {/* Assign Folder Dropdown */}
                          {selectedDocIdForFolder === doc.id ? (
                            <div className="flex items-center gap-1">
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleAssignToFolder(doc.id, e.target.value);
                                  }
                                }}
                                className="text-xs p-2 bg-zinc-50 border border-amber-300 focus:ring-2 focus:ring-amber-500/20 rounded-xl cursor-pointer font-bold outline-none shadow-sm"
                                defaultValue=""
                              >
                                <option value="" disabled>Classer dans...</option>
                                {foldersState.map(f => {
                                  const isAlreadyIn = fav?.folderIds?.some((fid: string) => String(fid).toLowerCase().trim() === String(f.id).toLowerCase().trim());
                                  return (
                                    <option key={f.id} value={f.id} disabled={isAlreadyIn}>
                                      {f.name} {isAlreadyIn ? '✓ (déjà dedans)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              <button
                                type="button"
                                onClick={() => setSelectedDocIdForFolder(null)}
                                className="p-2 text-zinc-400 hover:text-zinc-600 rounded-xl cursor-pointer"
                                title="Fermer"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedDocIdForFolder(doc.id)}
                              className="bg-white border border-zinc-200 hover:bg-amber-50 text-zinc-500 hover:text-amber-600 hover:border-amber-200 p-2.5 rounded-xl transition-all shadow-sm hover:shadow-md cursor-pointer flex items-center gap-1.5 text-xs font-bold"
                              title="Classer dans un dossier"
                            >
                              <FolderSymlink className="w-4 h-4 text-amber-500" />
                              <span className="hidden xl:inline">Classer</span>
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => navigate(`/document/${doc.id}`)}
                            className="bg-primary-50 border border-primary-100 hover:bg-primary-750 text-primary-750 hover:text-white p-2.5 rounded-xl cursor-pointer transition-all shadow-sm hover:shadow-md"
                            title="Consulter"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleFavorite(doc.id)}
                            className="bg-white border border-zinc-200 hover:bg-red-50 text-zinc-500 hover:text-red-600 hover:border-red-200 p-2.5 rounded-xl cursor-pointer transition-all shadow-sm hover:shadow-md"
                            title="Retirer des favoris"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. NOTES PRIVATE MANAGEMENT PANEL */}
      {activeTab === 'NOTES' && (
        <div className="animate-fade-in space-y-6">
          
          {/* Notes search filter */}
          <div className="bg-white border border-primary-750/10 rounded-3xl p-6 shadow-xl shadow-primary-750/5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="relative w-full max-w-md">
              <input
                type="text"
                placeholder="Rechercher dans mes notes..."
                value={notesSearch}
                onChange={(e) => setNotesSearch(e.target.value)}
                className="w-full text-sm font-semibold pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              />
              <Search className="w-5 h-5 text-zinc-400 absolute left-4 top-3" />
            </div>

            {filteredNotes.length > 0 && (
              <button
                onClick={() => handleSimulateExport('Recueil_Notes_DocArn', filteredNotes.map(n => `AUTEUR: Cabinet Associates\nDATE: ${formatFrenchDate(n.updatedAt)}\n${n.content}`).join("\n\n─────────────────────\n\n"))}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-primary-750 to-primary-600 hover:from-primary-800 hover:to-primary-700 text-white font-bold text-xs py-3 px-6 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer w-full sm:w-auto"
              >
                <FileDown className="w-4 h-4" /> Exporter le Recueil
              </button>
            )}
          </div>

          {groupedNotes.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-primary-750/10 rounded-3xl p-16 text-center space-y-4 shadow-sm flex flex-col items-center justify-center">
               <div className="bg-primary-50 p-4 rounded-full"><MessageSquareCode className="w-8 h-8 text-primary-500" /></div>
              <p className="text-sm font-semibold text-zinc-500">
                {notesSearch ? "Aucune note ne correspond à vos termes de recherche." : "Vous n'avez pas encore rédigé de notes privées. Pour annoter un texte, rendez-vous sur sa fiche détaillée !"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {groupedNotes.map((group) => (
                <div key={group.documentId} className="bg-white p-6 rounded-3xl border border-primary-750/10 shadow-sm space-y-4">
                  <div className="flex items-start justify-between border-b border-zinc-100 pb-4">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary-600 flex items-center gap-1.5">
                        <Star className="w-3 h-3"/> Rattaché à l'acte
                      </span>
                      <h5 className="font-display font-bold text-base text-zinc-900 line-clamp-2">
                        {group.documentTitle}
                      </h5>
                      {group.referenceNumber && (
                        <p className="text-[10px] text-zinc-500">{group.referenceNumber}</p>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/document/${group.documentId}`)}
                      className="text-[10px] font-bold uppercase tracking-wider bg-primary-50 text-primary-750 hover:bg-primary-750 hover:text-white px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                    >
                      Consulter l'acte
                    </button>
                  </div>

                  <div className="space-y-3">
                    {group.notes.map((note) => (
                      <div key={note.id} className="bg-zinc-50 border border-zinc-100 p-4 rounded-2xl relative">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed font-medium flex-1 relative z-10">
                            {note.content}
                          </p>
                          <button
                            onClick={() => noteService.deleteNoteById(note.id).then(success => success && setUserNotes(prev => prev.filter(n => n.id !== note.id)))}
                            className="text-zinc-400 hover:text-red-500 transition-colors p-1"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider flex items-center gap-1.5 pt-2">
                          <Clock className="w-3.5 h-3.5" /> {formatFrenchDate(note.updatedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3. HISTORIQUE RÉEL DES TÉLÉCHARGEMENTS ENREGISTRÉS */}
      {activeTab === 'DOWNLOADS' && (
        <div className="animate-fade-in space-y-6">
          {/* Header du groupe Téléchargements */}
          <div className="bg-white border border-violet-100 p-6 rounded-3xl shadow-lg shadow-violet-100/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-primary-750 text-white flex items-center justify-center shadow-md shadow-violet-500/20 shrink-0">
                <DownloadCloud className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-zinc-900">Historique de vos Téléchargements</h3>
                <p className="text-xs sm:text-sm font-medium text-zinc-500">
                  Retrouvez et téléchargez à nouveau les actes juridiques au format PDF enregistrés sur votre compte.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => loadUserDownloads()}
                disabled={loadingDownloads}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors cursor-pointer"
                title="Actualiser la liste"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingDownloads ? 'animate-spin text-violet-600' : ''}`} />
                <span>Actualiser</span>
              </button>
            </div>
          </div>

          {/* Barre de recherche dans les téléchargements */}
          {userDownloads.length > 0 && (
            <div className="bg-white border border-zinc-200/80 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:max-w-md">
                <input
                  type="text"
                  placeholder="Rechercher par titre, référence ou catégorie..."
                  value={downloadsSearch}
                  onChange={(e) => setDownloadsSearch(e.target.value)}
                  className="w-full text-xs font-semibold pl-10 pr-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400"
                />
                <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-3" />
                {downloadsSearch && (
                  <button
                    onClick={() => setDownloadsSearch('')}
                    className="absolute right-3 top-3 text-zinc-400 hover:text-zinc-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="text-xs font-medium text-zinc-500 self-end sm:self-center">
                <strong className="text-violet-700 font-bold">{filteredDownloads.length}</strong> acte{filteredDownloads.length > 1 ? 's' : ''} téléchargé{filteredDownloads.length > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Contenu de la liste */}
          {loadingDownloads ? (
            <div className="bg-white border border-zinc-200/80 rounded-3xl p-16 text-center space-y-3 shadow-sm flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
              <p className="text-xs font-semibold text-zinc-500">Chargement de votre historique de téléchargements...</p>
            </div>
          ) : userDownloads.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-zinc-200 rounded-3xl p-16 text-center space-y-4 shadow-sm flex flex-col items-center justify-center">
              <div className="w-14 h-14 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center">
                <DownloadCloud className="w-7 h-7" />
              </div>
              <div className="max-w-md space-y-1.5">
                <h4 className="font-display font-bold text-base text-zinc-900">Aucun document téléchargé</h4>
                <p className="text-xs text-zinc-500">
                  Lorsque vous téléchargez des actes juridiques au format PDF, ils s'enregistrent automatiquement dans cet espace pour que vous puissiez les consulter ou les récupérer à tout moment.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs shadow-md shadow-violet-600/20 transition-all cursor-pointer flex items-center gap-2"
              >
                <span>Explorer les actes juridiques</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : filteredDownloads.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-zinc-200 rounded-3xl p-12 text-center space-y-3 shadow-sm flex flex-col items-center justify-center">
              <p className="text-xs font-semibold text-zinc-500">
                Aucun document téléchargé ne correspond à votre recherche "{downloadsSearch}".
              </p>
              <button
                type="button"
                onClick={() => setDownloadsSearch('')}
                className="text-xs text-violet-600 hover:underline font-bold"
              >
                Réinitialiser la recherche
              </button>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200/80 rounded-3xl overflow-hidden shadow-sm divide-y divide-zinc-100">
              {filteredDownloads.map((dl) => {
                const isItemDownloading = downloadingDocId === String(dl.documentId);

                return (
                  <div
                    key={dl.id}
                    className="p-5 sm:p-6 hover:bg-violet-50/20 flex flex-col sm:flex-row sm:items-center justify-between gap-5 transition-all group"
                  >
                    <div className="space-y-2 flex-grow min-w-0">
                      {/* Métadonnées & Badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        {dl.referenceNumber && (
                          <span className="text-[11px] font-mono font-bold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-md">
                            Réf : {dl.referenceNumber}
                          </span>
                        )}
                        {dl.categoryName && (
                          <span className="text-[10px] font-bold text-zinc-600 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-md">
                            {dl.categoryName}
                          </span>
                        )}
                        {dl.legalStatus && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                            {dl.legalStatus}
                          </span>
                        )}
                        <span className="text-[10px] font-medium text-zinc-400 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-zinc-400" />
                          Téléchargé le {formatFrenchDate(dl.downloadedAt)}
                        </span>
                      </div>

                      {/* Titre officiel du document */}
                      <h5
                        onClick={() => navigate(`/document/${dl.documentId}`)}
                        className="font-display font-bold text-sm sm:text-base text-zinc-900 leading-snug group-hover:text-violet-700 transition-colors cursor-pointer"
                        title="Cliquer pour consulter la fiche complète"
                      >
                        {dl.officialTitle || "Document juridique"}
                      </h5>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <button
                        type="button"
                        onClick={() => navigate(`/document/${dl.documentId}`)}
                        className="px-3 py-2 rounded-xl text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                        title="Consulter la fiche"
                      >
                        <Eye className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="hidden md:inline">Consulter</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDownloadAgain(dl)}
                        disabled={isItemDownloading}
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer ${
                          isItemDownloading
                            ? 'bg-zinc-300 text-zinc-600 cursor-not-allowed'
                            : 'bg-violet-600 hover:bg-violet-700 text-white hover:shadow-md'
                        }`}
                        title="Télécharger à nouveau le PDF"
                      >
                        {isItemDownloading ? (
                          <>
                            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Téléchargement...</span>
                          </>
                        ) : (
                          <>
                            <DownloadCloud className="w-3.5 h-3.5" />
                            <span>Télécharger (PDF)</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 4. REQ TRACKER SUBMISSIONS AND STATUS BADGES */}
      {activeTab === 'SUGGESTIONS' && (
        <div className="animate-fade-in space-y-6">
          <div className="bg-white border border-violet-100 p-6 rounded-3xl shadow-lg shadow-violet-100/30 flex items-center gap-4">
            <div className="bg-violet-100 p-3 rounded-full"><AlertOctagon className="w-6 h-6 text-violet-600"/></div>
            <div>
              <h3 className="font-display font-bold text-lg text-violet-900">Suivi de vos Requêtes d'Indexation</h3>
              <p className="text-sm font-medium text-zinc-500">Suivez l'avancement des textes manquants que vous avez signalés à notre cellule documentaire.</p>
            </div>
          </div>

          {loadingSuggestions ? (
            <div className="bg-white border border-primary-750/10 rounded-3xl p-16 text-center space-y-3 shadow-sm flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
              <p className="text-xs font-semibold text-zinc-500">Chargement de vos requêtes...</p>
            </div>
          ) : userSuggestions.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-primary-750/10 rounded-3xl p-16 text-center space-y-4 shadow-sm flex flex-col items-center justify-center">
              <div className="bg-violet-50 p-4 rounded-full"><AlertOctagon className="w-8 h-8 text-violet-600" /></div>
              <p className="text-sm font-semibold text-zinc-500">
                Vous n'avez pas encore soumis de requête d'indexation.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-primary-750/10 rounded-3xl overflow-hidden shadow-xl shadow-primary-750/5">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-zinc-50/50 border-b border-zinc-100 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                    <tr>
                      <th className="p-5 font-semibold">Date de Dépôt</th>
                      <th className="p-5 font-semibold">Nature</th>
                      <th className="p-5 font-semibold">Référence Légale</th>
                      <th className="p-5 font-semibold w-5/12">Titre / Objet</th>
                      <th className="p-5 font-semibold text-right">Statut du traitement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {userSuggestions.map((ticket) => (
                      <tr key={ticket.id} className="hover:bg-purple-50/30 transition-colors group">
                        <td className="p-5">
                          <span className="text-[11px] font-bold text-zinc-600 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-zinc-400"/>
                            {formatFrenchDate(ticket.submittedAt).split(' ').slice(0, 3).join(' ')}
                          </span>
                        </td>
                        <td className="p-5">
                          <span className="font-bold text-primary-750 bg-primary-50 border border-primary-200/50 px-2 py-1 rounded-md text-[10px] uppercase shadow-sm">
                            {ticket.nature}
                          </span>
                        </td>
                        <td className="p-5">
                          <span className="font-mono text-xs font-bold text-zinc-600 bg-zinc-100 border border-zinc-200 px-2.5 py-1 rounded-md">
                            {ticket.reference}
                          </span>
                        </td>
                        <td className="p-5">
                          <div className="font-semibold text-sm text-zinc-800 line-clamp-1 group-hover:text-primary-750 transition-colors" title={ticket.title}>
                            {ticket.title}
                          </div>
                        </td>
                        <td className="p-5 text-right">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase shadow-sm border ${
                            ticket.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                            ticket.status === 'REJECTED' ? 'bg-red-50 text-red-700 border-red-200' : 
                            'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              ticket.status === 'APPROVED' ? 'bg-emerald-500' : 
                              ticket.status === 'REJECTED' ? 'bg-red-500' : 
                              'bg-amber-500 animate-pulse'
                            }`}></span>
                            {ticket.status === 'APPROVED' ? 'Indexé' : ticket.status === 'REJECTED' ? 'Rejeté' : 'En examen'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5. SECURITY & PASSWORD MANAGEMENT PANEL */}
      {activeTab === 'SECURITY' && (
        <div className="animate-fade-in space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Profile Overview Card */}
            <div className="lg:col-span-5 bg-white border border-primary-750/10 shadow-xl shadow-primary-750/5 rounded-3xl p-6 space-y-6">
              <div className="border-b border-primary-750/10 pb-4">
                <h4 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-violet-600" /> Profil de l'utilisateur
                </h4>
                <p className="text-xs text-zinc-500 mt-1">Détails de votre compte et droits d'accès.</p>
              </div>

              <div className="space-y-4 text-xs">
                <div className="p-4 bg-violet-50/50 border border-violet-100 rounded-2xl flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-violet-700 text-white font-bold flex items-center justify-center text-lg shadow-sm">
                    {(currentUser?.fullName || currentUser?.email || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-zinc-900">{currentUser?.fullName || 'Utilisateur'}</div>
                    <div className="text-zinc-500">{currentUser?.email}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold text-zinc-400 block">Type de compte</span>
                    <span className="font-semibold text-zinc-800">{currentUser?.role || 'Abonné'}</span>
                  </div>
                  <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold text-zinc-400 block">Cabinet / Entité</span>
                    <span className="font-semibold text-zinc-800">{currentUser?.cabinetName || 'Cabinet'}</span>
                  </div>
                </div>

                <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-xl">
                  <span className="text-[10px] uppercase font-bold text-zinc-400 block">Identifiant système</span>
                  <span className="font-mono text-[11px] text-zinc-600 break-all">{currentUser?.nameid || '-'}</span>
                </div>
              </div>
            </div>

            {/* Change Password Card */}
            <div className="lg:col-span-7 bg-white border border-primary-750/10 shadow-xl shadow-primary-750/5 rounded-3xl p-6 space-y-6">
              <div className="border-b border-primary-750/10 pb-4">
                <h4 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-violet-600" /> Modifier mon mot de passe
                </h4>
                <p className="text-xs text-zinc-500 mt-1">Mettez à jour le mot de passe de votre compte en toute sécurité.</p>
              </div>

              {pwdError && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-xs leading-relaxed animate-fade-in">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwdError}</span>
                </div>
              )}

              {pwdSuccess && (
                <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-3 rounded-xl text-xs leading-relaxed animate-fade-in">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwdSuccess}</span>
                </div>
              )}

              <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-600 uppercase tracking-wider mb-1.5">
                    Mot de passe actuel
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      required
                      placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none cursor-pointer p-0.5 bg-transparent border-none flex items-center justify-center"
                      title={showCurrentPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 uppercase tracking-wider mb-1.5">
                      Nouveau mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        required
                        minLength={6}
                        placeholder="Min. 6 caractères"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none cursor-pointer p-0.5 bg-transparent border-none flex items-center justify-center"
                        title={showNewPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 uppercase tracking-wider mb-1.5">
                      Confirmer le nouveau mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        minLength={6}
                        placeholder="Répétez le mot de passe"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none cursor-pointer p-0.5 bg-transparent border-none flex items-center justify-center"
                        title={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={pwdLoading}
                    className="px-6 py-2.5 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-semibold text-xs rounded-xl transition-all cursor-pointer border-none disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-violet-700/20 flex items-center gap-2"
                  >
                    {pwdLoading ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Mise à jour...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Enregistrer le nouveau mot de passe
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};